import { request } from '../../api'
import { useServerFetch } from '../../composables/useServerFetch'

export type ApiFiat = {
  name?: string
  code?: string
}

export type ApiFundingItem = {
  code?: string
  tradeType?: string
  fiats?: ApiFiat[] | null
  paymentMethod?: string
  minAmount?: string
  inOutType?: number
}

export type ApiDescItem = {
  title?: string
  desc?: string
}

export type ApiFundingDesc = {
  title?: string
  list?: ApiDescItem[]
}

export type ApiFundingTab = {
  title?: string
  type?: number
  list?: ApiFundingItem[]
  desc?: ApiFundingDesc
}

export type ApiFundingSection = {
  title?: string
  type?: number
  tabs?: ApiFundingTab[]
}

export type ApiFundingResponse = {
  code?: number
  data?: ApiFundingSection[]
  message?: string
  succeed?: boolean
}

export type ApiFundingDetailParams = {
  exchangeId: string
  type: number
  tabsType: number
  fiats: string
  code: string
}

type ApiFundingDetailResponse = {
  code?: number
  data?: ApiFundingItem
  message?: string
  succeed?: boolean
}

type ApiFundingResult = ApiFundingResponse | ApiFundingSection[] | null
type ApiFundingDetailResult = ApiFundingDetailResponse | ApiFundingItem | null

export type FeeRateParams = {
  exchangeId: string
  tabsCode: string | undefined
  subTabsCode: string | undefined
}

export type FeeRateApiTab = {
  title?: string
  code?: string
  subTabs?: FeeRateApiTab[]
}

export type FeeRateApiRow = {
  icon?: string
  code?: string
  name?: string
  level?: string
  networkName?: string
  networkFee?: string
  minAmount?: string
  [key: string]: unknown
}

export type FeeRateApiTable = {
  header?: string[]
  data?: FeeRateApiRow[]
}

export type VipFeeRateTip = {
  title?: string // VIP 等级
  desc?: string // VIP 费率提示文案
}

export type GetVipFeeRateTipsResponse = {
  tips?: VipFeeRateTip[] // VIP 费率提示列表
}

export type OfficialSiteJumpContext = {
  isOpen?: boolean // 是否存在可用官方网址
  primaryWebUrl?: string // 首个官网展示 URL，展示/复制使用
  primaryOriginalUrl?: string // 首个官网原始长链，跳转优先使用
  isLegal?: boolean // 首个官网合法性
  isSpecial?: boolean // 是否特殊占位
  tips?: { content: string }[] // 特别提示弹窗文案
  isCooperativeCustomer?: boolean // 是否合作客户
  isUltimate?: boolean // 是否旗舰店
  isEpc?: boolean // 是否保障客户
  isWhite?: boolean // 是否白名单
  isKyb?: boolean // 是否 KYB
  totalScore?: string // 天眼总分
  pureLScore?: string // 监管评分
  isScoreAboveThreshold?: boolean // 双评分是否均大于等于 3
  isMainlandChina?: boolean // 是否中国大陆用户
  isFirstTierCity?: boolean // 是否一线城市 IP
  isWebsiteHidden?: boolean // TDE后台是否隐藏
}

export type OfficialSiteJumpContextParams = {
  traderCode?: string // 交易商编码
  ip?: string // 客户端 IP；Brand 一线城市判断依赖 query 参数
  languageCode?: string // 语言，如 zh-CN
  countryCode?: string // 国家/地区，如 156
}

type OfficialSiteJumpContextApiResponse =
  | OfficialSiteJumpContext
  | {
      code?: number
      data?: OfficialSiteJumpContext | null
      message?: string
      succeed?: boolean
    }
  | null

export type FeeRateApiPayload = {
  tabs?: FeeRateApiTab[]
  table?: FeeRateApiTable | null
}

type GetVipFeeRateTipsApiResponse =
  | GetVipFeeRateTipsResponse
  | {
      code?: number
      data?: GetVipFeeRateTipsResponse | null
      message?: string
      succeed?: boolean
    }
  | null

type FeeRateApiResponse =
  | {
      code?: number
      data?: FeeRateApiPayload | null
      message?: string
      succeed?: boolean
    }
  | FeeRateApiPayload
  | null

export type RatingTrendPoint = {
  month?: string
  value?: string
}

export type RatingTrendSeries = {
  color?: string
  isIndustry?: boolean
  items?: RatingTrendPoint[]
  name?: string
}

export type RatingChartType = 'active' | 'opinion'

export type RatingChartParams = {
  exchangeId: string
  range: number
  type: RatingChartType
}

export type RatingChartPayload = {
  range?: number
  series?: RatingTrendSeries[]
  type?: RatingChartType
}

export type RatingIndexSelectItem = {
  checked?: boolean
  code?: string
  label?: string
  name?: string
  title?: string
  value?: string | number
}

export type RatingDeductionItem = {
  content?: string
  icon?: string
}

export type RatingBusinessItem = {
  content?: string
  country?: string
  cover?: string
  icon?: string
  labels?: {
    backgroundColor?: string
    borderColor?: string
    fontColor?: string
    text?: string
    type?: number
  }[]
  level?: string
  location?: string
  name?: string
  sid?: string
  status?: string
  summary?: string
  title?: string
  type?: number
  vrIcon?: string
}

export type RatingBusinessDetail = {
  items?: RatingBusinessItem[]
  title?: string
}

export type RatingIapItem = {
  title?: string
  value?: string
  valueColor?: string
}

export type RatingIapDetail = {
  amount?: string
  amountIcon?: string
  bgColors?: string[] | null
  borderColors?: string[] | null
  guardType?: number
  items?: RatingIapItem[]
  tips?: string
  title?: string
}

export type RatingTechnicalDetail = {
  content?: string[]
  level?: number
  levelContent?: string
}

export type RatingAdjustmentDetail = {
  business?: RatingBusinessDetail | RatingBusinessItem | RatingBusinessItem[] | string | null
  content?: string
  deductions?: RatingDeductionItem[] | null
  iap?: RatingIapDetail | null
  technical?: RatingTechnicalDetail | null
}

export type RatingAdjustmentItem = {
  adjustment?: number | string
  adjustmentColor?: string
  adjustmentDetail?: RatingAdjustmentDetail | null
  adjustmentIcon?: string
  adjustmentText?: string
}

export type RatingSmallScore = {
  adjustment?: number | string
  adjustmentColor?: string
  adjustments?: RatingAdjustmentItem[] | null
  adjustmentDetail?: RatingAdjustmentDetail | null
  adjustmentIcon?: string
  adjustmentText?: string
  bgColor?: string
  cate?: string
  desc?: string
  level?: string
  name?: string
  total?: string
}

export type RatingMainPayload = {
  bgColor?: string
  dateTime?: string
  ico?: string
  level?: string
  levelBgColorIntl?: string
  name?: string
  smallScores?: RatingSmallScore[]
  total?: string
}

export type RatingRiskDetail = {
  content?: string
  icon?: string
}

export type RatingRiskPayload = {
  color?: string
  details?: RatingRiskDetail[]
  title?: string
  total?: number
}

export type RatingDetectItem = {
  dimensionName?: string
  flag?: string
  image?: string
  indexName?: string
  value?: string
}

export type RatingDetailPayload = {
  activeIndex?: RatingTrendSeries[]
  detects?: RatingDetectItem[]
  globalOpinionIndex?: RatingTrendSeries[]
  indexSelectList?: RatingIndexSelectItem[]
  instructionExecutionIndex?: RatingTrendSeries[]
  main?: RatingMainPayload | null
  risk?: RatingRiskPayload | null
}

type RatingDetailApiResponse =
  | {
      code?: number
      data?: RatingDetailPayload | null
      message?: string
      succeed?: boolean
    }
  | RatingDetailPayload
  | null

type RatingChartApiResponse =
  | {
      code?: number
      data?: RatingChartPayload | null
      message?: string
      succeed?: boolean
    }
  | RatingChartPayload
  | null

const extractFundingSections = (response: ApiFundingResult) => {
  if (Array.isArray(response)) {
    return response
  }

  return Array.isArray(response?.data) ? response.data : []
}

const extractFundingDetail = (response: ApiFundingDetailResult): ApiFundingItem | null => {
  if (!response || typeof response !== 'object') {
    return null
  }

  if ('data' in response) {
    return response.data || null
  }

  return response as ApiFundingItem
}

const extractFeeRatePayload = (response: unknown): FeeRateApiPayload | null => {
  if (!response || typeof response !== 'object') {
    return null
  }

  const normalizedResponse = response as { data?: FeeRateApiPayload | null }

  if ('data' in response) {
    return normalizedResponse.data ?? null
  }

  return response as FeeRateApiPayload
}

const extractVipFeeRateTipsPayload = (response: GetVipFeeRateTipsApiResponse): GetVipFeeRateTipsResponse | null => {
  if (!response || typeof response !== 'object') {
    return null
  }

  const normalizedResponse = response as { data?: GetVipFeeRateTipsResponse | null }

  if ('data' in response) {
    return normalizedResponse.data ?? null
  }

  return response as GetVipFeeRateTipsResponse
}

const extractRatingDetailPayload = (response: RatingDetailApiResponse): RatingDetailPayload | null => {
  if (!response || typeof response !== 'object') {
    return null
  }

  const normalizedResponse = response as { data?: RatingDetailPayload | null }

  if ('data' in response) {
    return normalizedResponse.data ?? null
  }

  return response as RatingDetailPayload
}

const extractRatingChartPayload = (response: RatingChartApiResponse): RatingChartPayload | null => {
  if (!response || typeof response !== 'object') {
    return null
  }

  const normalizedResponse = response as { data?: RatingChartPayload | null }

  if ('data' in response) {
    return normalizedResponse.data ?? null
  }

  return response as RatingChartPayload
}

const extractOfficialSiteJumpContext = (
  response: OfficialSiteJumpContextApiResponse,
): OfficialSiteJumpContext | null => {
  if (!response || typeof response !== 'object') {
    return null
  }

  const normalizedResponse = response as { data?: OfficialSiteJumpContext | null }

  if ('data' in response) {
    return normalizedResponse.data ?? null
  }

  return response as OfficialSiteJumpContext
}

const getExchangeDepositWithdrawalListByType = async (exchangeId: string, type: number) => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/trader/detail/deposit-withdrawal/list',
    method: 'GET',
    params: {
      exchangeId,
      type,
    },
    headers: {
      CountryCode: '156',
      LanguageCode: 'zh-CN',
    },
  } as const

  const response = import.meta.server
    ? await useServerFetch<ApiFundingResponse>(requestConfig)
    : ((await request<ApiFundingResult>(requestConfig)) as unknown as ApiFundingResult)

  return extractFundingSections(response)
}

export const getExchangeDepositWithdrawalList = async (exchangeId: string) => {
  const responses = await Promise.all([
    getExchangeDepositWithdrawalListByType(exchangeId, 0),
    getExchangeDepositWithdrawalListByType(exchangeId, 1),
  ])

  return responses.flat()
}

export const getExchangeDepositWithdrawalDetail = async (params: ApiFundingDetailParams) => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/trader/detail/deposit-withdrawal/detail',
    method: 'GET',
    params,
    headers: {
      CountryCode: '156',
      LanguageCode: 'zh-CN',
    },
  } as const

  const response = import.meta.server
    ? await useServerFetch<ApiFundingDetailResponse>(requestConfig)
    : ((await request<ApiFundingDetailResult>(requestConfig)) as unknown as ApiFundingDetailResult)

  return extractFundingDetail(response)
}

export const getFeeRate = async (params: FeeRateParams) => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/market/feerate',
    method: 'GET',
    params,
  } as const

  const response = import.meta.server
    ? await useServerFetch<FeeRateApiResponse>(requestConfig)
    : await request<FeeRateApiResponse>(requestConfig)

  return extractFeeRatePayload(response)
}

export const getRatingDetail = async (exchangeId: string): Promise<RatingDetailPayload | null> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/wikibit/getRatingDetail',
    method: 'GET',
    params: {
      exchangeId,
    },
  } as const

  const response = import.meta.server
    ? await useServerFetch<RatingDetailApiResponse>(requestConfig)
    : await request<RatingDetailApiResponse>(requestConfig)

  return extractRatingDetailPayload(response as RatingDetailApiResponse)
}

export const getRatingChart = async (params: RatingChartParams): Promise<RatingChartPayload | null> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: api.v4,
    url: '/api/v4/bit/wikibit/getRatingChart',
    method: 'GET',
    params,
  } as const

  if (import.meta.server) {
    return extractRatingChartPayload(await useServerFetch<RatingChartApiResponse>(requestConfig))
  }

  return extractRatingChartPayload(await request<RatingChartApiResponse>(requestConfig))
}

export const getVipFeerateTips = async (): Promise<GetVipFeeRateTipsResponse | null> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/market/vipfeeratetips',
    method: 'GET',
  } as const

  const response = import.meta.server
    ? await useServerFetch<GetVipFeeRateTipsApiResponse>(requestConfig)
    : await request<GetVipFeeRateTipsApiResponse>(requestConfig)

  return extractVipFeeRateTipsPayload(response as GetVipFeeRateTipsApiResponse)
}

/**
 * 获取 Web 进入官网跳转上下文。
 * BFF 只返回判断所需上下文，最终打开、倒计时、复制或禁止访问策略由前端处理。
 */
export const getOfficialSiteJumpContext = async (
  params: OfficialSiteJumpContextParams,
): Promise<OfficialSiteJumpContext | null> => {
  const api = useEnvApi()
  // ip 必须作为 query 参数传递，Brand 不支持通过 Header 接收 IP。
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/trader/detail/official-site/jump-context',
    method: 'GET',
    params,
  } as const

  const response = import.meta.server
    ? await useServerFetch<OfficialSiteJumpContextApiResponse>(requestConfig)
    : await request<OfficialSiteJumpContextApiResponse>(requestConfig)

  return extractOfficialSiteJumpContext(response as OfficialSiteJumpContextApiResponse)
}

export const getClientIp = async () => {
  // 进入官网策略需要真实客户端 IP，用于 BFF/Brand 判断大陆一线城市。
  const res = await fetch(`https://exportedbase.${TECH002}/ip`)
  const data = await res.json()
  return data?.result?.ip || ''
}

export type SubmitFeedbackImmediateParams = {
  commentGradeId?: string
  userId?: string
  code?: string
  content?: string
  images?: string[]
  objectType?: number
}

export const submitFeedbackImmediate = async (params: SubmitFeedbackImmediateParams) => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/feedback/immediate',
    method: 'POST',
    data: params,
  } as const

  return import.meta.server ? useServerFetch(requestConfig) : request(requestConfig)
}

export type GetFeedbackFinishTipsParams = {
  code: string
  isFirst: boolean
}

export const getFeedbackFinishTips = async (params: GetFeedbackFinishTipsParams) => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/feedback/postfeedbackfinishtips',
    method: 'GET',
    params,
  } as const

  return import.meta.server ? useServerFetch(requestConfig) : request(requestConfig)
}

const VALIDATE_FEEDBACK_GEETEST_ROUTE = 'wikicommunity$validategeettestorcode4'

export type ValidateFeedbackGeetestParams = {
  lot_number: string
  captcha_output: string
  pass_token: string
  gen_time: string
  userId: string
  code: string
  type: number
  cide: string
}

/** 反馈发布极验二次校验，对齐 web-wikifxcom 曝光发布 common.CV3 调用。 */
export const validateFeedbackGeetest = async (params: ValidateFeedbackGeetestParams) => {
  const api = useEnvApi()

  return request({
    baseURL: api.homeClient,
    url: `${WIKIBITWEBV1}/invoke-v3/v3`,
    method: 'POST',
    data: params,
    headers: {
      Routes: VALIDATE_FEEDBACK_GEETEST_ROUTE,
    },
  })
}

const APPEND_USER_INFO_ROUTE = 'wikifxforum$wikifxforum_appenduserinfo'

export type AppendForumUserInfoParams = {
  code: string
  name: string
  account: string
  requirements: string
  amount: number
  languageCode: string
  countryCode: string
  telphone: string
  email: string
  title: string
  content: string
  category: string
  images: string[]
}

/** 合作交易所调解：补充用户信息到 WikiFX forum 反馈单。 */
export const appendForumUserInfo = async (params: AppendForumUserInfoParams) => {
  const api = useEnvApi()

  return request({
    baseURL: api.homeClient,
    url: `/${WIKIBITWEBV1}/invoke-v4/${APPEND_USER_INFO_ROUTE}`,
    method: 'POST',
    data: {
      [`${APPEND_USER_INFO_ROUTE}#0`]: params,
    },
    headers: {
      Routes: `${APPEND_USER_INFO_ROUTE}#0`,
      fxintl: true,
      pkgname: 'com.wikifx.bit',
      appchannel: 'ziji',
    },
  })
}

export const getPostFeedbackSetting = async () => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/feedback/postfeedbacksetting',
    method: 'GET',
  } as const

  return import.meta.server ? useServerFetch(requestConfig) : request(requestConfig)
}

/** 检查当前用户是否已监控（收藏）指定交易所。 */
export type CheckExchangeFavoriteParams = {
  exchangeCode: string
}

export type CheckExchangeFavoriteResponse = {
  code?: number
  data?: {
    isFavorite?: boolean
  } | null
  message?: string
  succeed?: boolean
}

export const checkExchangeFavorite = async (params: CheckExchangeFavoriteParams) => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/brand/exchange-favorite/check',
    method: 'GET',
    params,
  } as const

  return import.meta.server
    ? useServerFetch<CheckExchangeFavoriteResponse>(requestConfig)
    : request<CheckExchangeFavoriteResponse>(requestConfig)
}
